import {Card, CardContent, Typography, Box, Skeleton} from '@mui/material';
import {TrendingUp, TrendingDown, TrendingFlat} from '@mui/icons-material';

interface OverallStatsCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  loading?: boolean;
  trend?: 'up' | 'down' | 'flat';
}

export default function OverallStatsCard({
  title,
  value,
  subtitle,
  color,
  loading = false,
  trend,
}: OverallStatsCardProps) {
  const getColorValue = (colorType: string) => {
    switch (colorType) {
      case 'primary':
        return '#1976d2';
      case 'secondary':
        return '#9c27b0';
      case 'success':
        return '#2e7d32';
      case 'error':
        return '#d32f2f';
      case 'warning':
        return '#ed6c02';
      case 'info':
        return '#0288d1';
      default:
        return '#1976d2';
    }
  };

  const getTrendIcon = (trendType?: string) => {
    switch (trendType) {
      case 'up':
        return <TrendingUp sx={{fontSize: 16, color: 'success.main'}} />;
      case 'down':
        return <TrendingDown sx={{fontSize: 16, color: 'error.main'}} />;
      case 'flat':
        return <TrendingFlat sx={{fontSize: 16, color: 'text.secondary'}} />;
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${getColorValue(color)}15 0%, ${getColorValue(color)}05 100%)`,
        border: `1px solid ${getColorValue(color)}30`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{p: 3}}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2}}>
          <Typography variant="body2" color="text.secondary" sx={{fontWeight: 500}}>
            {title}
          </Typography>
          {getTrendIcon(trend)}
        </Box>

        {loading ? (
          <Box>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
        ) : (
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: getColorValue(color),
                mb: 1,
                lineHeight: 1,
              }}
            >
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
